import { T } from '@tolgee/react';
import { without } from 'es-toolkit';
import { useEffect, useState } from 'react';
import { HiOutlineGlobeAmericas } from 'react-icons/hi2';
import { IoTelescopeOutline } from 'react-icons/io5';
import { TbBook, TbMath } from 'react-icons/tb';
import { useWindowSize } from 'usehooks-ts';

export function useLessonSuggestions(): {
  suggestions: LessonSuggestion[];
  shuffleSuggestions: () => void;
} {
  const { width } = useWindowSize();
  const numOptions = width >= 768 ? 3 : width < 480 ? 1 : 2;
  const [randomSuggestions, setRandomSuggestions] = useState(() => {
    const shuffled = [...flexibleLessonSuggestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, numOptions);
  });

  const shuffleSuggestions = () => {
    const shuffled = [...flexibleLessonSuggestions].sort(() => 0.5 - Math.random());
    setRandomSuggestions(shuffled.slice(0, numOptions));
    return;
  };
  useEffect(() => {
    if (width >= 480 && randomSuggestions.length < numOptions) {
      setRandomSuggestions(prev => [
        ...prev,
        ...without(flexibleLessonSuggestions, ...prev)
          .sort(() => 0.5 - Math.random())
          .slice(0, numOptions - prev.length),
      ]);
    } else if (width < 768 && randomSuggestions.length > numOptions) {
      setRandomSuggestions(prev => prev.slice(0, numOptions));
    }
  }, [width, randomSuggestions, numOptions]);

  return {
    suggestions: randomSuggestions,
    shuffleSuggestions,
  };
}

export type LessonSuggestion = {
  summary: React.ReactNode;
  promptKey: string;
  promptDefault: string;
  icon: React.ReactNode;
};

const flexibleLessonSuggestions: LessonSuggestion[] = [
  {
    summary: (
      <T keyName="summary_prompt_example_11" defaultValue="The Giver Novel Study" />
    ),
    promptKey: 'prompt_example_11',
    promptDefault:
      '8th grade novel study on The Giver by Lois Lowry, chapter 5. Focus on theme. 45 minutes.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbBook size={16} />,
  },
  {
    summary: <T keyName="summary_prompt_example_12" defaultValue="Sensory Imagery" />,
    promptKey: 'prompt_example_12',
    promptDefault:
      '4th grade writing lesson. 30 minutes. Use sensory imagery to convey a sequence of events about an after-school hobby.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbBook size={16} />,
  },
  {
    summary: (
      <T
        keyName="summary_prompt_example_13"
        defaultValue="The American Dream in The Great Gatsby"
      />
    ),
    promptKey: 'prompt_example_13',
    promptDefault:
      '11th grade ELA lesson. The Great Gatsby by F. Scott Fitzgerald. Focus on analyzing the American Dream. 50 minutes.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbBook size={16} />,
  },
  {
    summary: (
      <T
        keyName="summary_prompt_example_14"
        defaultValue="Analyze Foreshadowing in Macbeth"
      />
    ),
    promptKey: 'prompt_example_14',
    promptDefault:
      '9th grade ELA. Macbeth Act III, Scene IV read aloud lesson. Analyze foreshadowing. 43 minutes.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbBook size={16} />,
  },
  {
    summary: <T keyName="summary_prompt_example_15" defaultValue="Making Inferences" />,
    promptKey: 'prompt_example_15',
    promptDefault:
      '6th grade 20 minute read aloud lesson on The Hunger Games, chapter 7 focusing on making inferences.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbBook size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_16" defaultValue="The Civil Rights Movement" />
    ),
    promptKey: 'prompt_example_16',
    promptDefault:
      '10th grade U.S. History lesson. The Civil Rights Movement. Analyze the strategies of leaders like Martin Luther King Jr. 30 minutes.',
    // @ts-expect-error react-icons type mismatch
    icon: <HiOutlineGlobeAmericas size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_17" defaultValue="Ancient Civilizations" />
    ),
    promptKey: 'prompt_example_17',
    promptDefault:
      'How geography influenced the development of the ancient Mayan city of Tikal for 6th graders. I have 32 minutes.',
    // @ts-expect-error react-icons type mismatch
    icon: <HiOutlineGlobeAmericas size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_18" defaultValue="The Battle of the Alamo" />
    ),
    promptKey: 'prompt_example_18',
    promptDefault: '25 minutes lesson on The Battle of the Alamo for 4th graders.',
    // @ts-expect-error react-icons type mismatch
    icon: <HiOutlineGlobeAmericas size={16} />,
  },
  {
    summary: <T keyName="summary_prompt_example_19" defaultValue="Writing Practice" />,
    promptKey: 'prompt_example_19',
    promptDefault: 'Practice hyperbole in writing in 10 minutes for 7th grade.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbBook size={16} />,
  },
  {
    summary: <T keyName="summary_prompt_example_20" defaultValue="Figurative Language" />,
    promptKey: 'prompt_example_20',
    promptDefault:
      'Analyze examples of figurative language. 15 minute lesson for 9th graders.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbBook size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_21" defaultValue="Describe Quadrilaterals" />
    ),
    promptKey: 'prompt_example_21',
    promptDefault:
      '5th math; practice using attributes to classify quadrilaterals; 15 minutes',
    // @ts-expect-error react-icons type mismatch
    icon: <TbMath size={16} />,
  },
  {
    summary: <T keyName="summary_prompt_example_22" defaultValue="Ratios in the NBA" />,
    promptKey: 'prompt_example_22',
    promptDefault:
      '20 min; identify and represent equivalent ratios in NBA stats; 6th grade',
    // @ts-expect-error react-icons type mismatch
    icon: <TbMath size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_23" defaultValue="Two-step Linear Equations" />
    ),
    promptKey: 'prompt_example_23',
    promptDefault:
      'Practice solving linear equations in the form p(x+q)=y for 7th grade math; I have 23 minutes',
    // @ts-expect-error react-icons type mismatch
    icon: <TbMath size={16} />,
  },
  {
    summary: <T keyName="summary_prompt_example_24" defaultValue="Grouping by Tens" />,
    promptKey: 'prompt_example_24',
    promptDefault: '50 minutes on adding and subtracting ten within 100. 1st grade.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbMath size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_25" defaultValue="Area Model Multiplication" />
    ),
    promptKey: 'prompt_example_25',
    promptDefault:
      '25 minute lesson on multiplying 2-digit by 2-digit numbers using the area model; 3rd grade.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbMath size={16} />,
  },
  {
    summary: (
      <T
        keyName="summary_prompt_example_26"
        defaultValue="Relative Frequency of Going Viral"
      />
    ),
    promptKey: 'prompt_example_26',
    promptDefault:
      'Use conditional relative frequencies to find which variable shows the strongest association with Going viral on Tik Tok. 8th math. 27 minutes.',
    // @ts-expect-error react-icons type mismatch
    icon: <TbMath size={16} />,
  },
  {
    summary: (
      <T
        keyName="summary_prompt_example_27"
        defaultValue="Energy Flow in the Texas Hill Country"
      />
    ),
    promptKey: 'prompt_example_27',
    promptDefault:
      '7th grade. Use the 10% rule to model energy flow across trophic levels in the Texas hill country.',
    // @ts-expect-error react-icons type mismatch
    icon: <IoTelescopeOutline size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_28" defaultValue="Conservation of Matter" />
    ),
    promptKey: 'prompt_example_28',
    promptDefault:
      '25 min; use the law of conservation of matter to identify balanced and unbalanced chemical equations. 8th grade.',
    // @ts-expect-error react-icons type mismatch
    icon: <IoTelescopeOutline size={16} />,
  },
  {
    summary: <T keyName="summary_prompt_example_29" defaultValue="Lab Safety" />,
    promptKey: 'prompt_example_29',
    promptDefault:
      '10 minute orientation to lab safety for chemical experiments; 9th graders',
    // @ts-expect-error react-icons type mismatch
    icon: <IoTelescopeOutline size={16} />,
  },
  {
    summary: (
      <T keyName="summary_prompt_example_30" defaultValue="Modeling the Rock Cycle" />
    ),
    promptKey: 'prompt_example_30',
    promptDefault:
      'Model the rock cycle, labeling the rock attributes and type of change; 35 minutes; 3rd grade',
    // @ts-expect-error react-icons type mismatch
    icon: <IoTelescopeOutline size={16} />,
  },
];
